import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Person,
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Refresh,
  Visibility,
  Star,
  StarBorder
} from '@mui/icons-material';
import apiClient from '../../api';

const DriverSafetyScores = () => {
  const [safetyScores, setSafetyScores] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState('30'); // days
  const [sortBy, setSortBy] = useState('score'); // score, name, trend
  const [viewMode, setViewMode] = useState('table'); // table, list

  useEffect(() => {
    loadSafetyData();
  }, [dateRange]);

  const loadSafetyData = async () => {
    try {
      setLoading(true);
      setError(null);

      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateRange));

      // Load safety scores and drivers in parallel
      const [safetyResponse, driversResponse] = await Promise.allSettled([
        apiClient.get(`/samsara/drivers/safety-scores?startTime=${startDate.toISOString()}&endTime=${endDate.toISOString()}`),
        apiClient.get('/samsara/drivers')
      ]);

      if (safetyResponse.status === 'fulfilled') {
        setSafetyScores(safetyResponse.value.data.data || []);
      } else {
        console.warn('Failed to load safety scores:', safetyResponse.reason);
        setSafetyScores([]);
      }

      if (driversResponse.status === 'fulfilled') {
        setDrivers(driversResponse.value.data.data || []);
      } else {
        console.warn('Failed to load drivers:', driversResponse.reason);
        setDrivers([]);
      }
    } catch (err) {
      console.error('Error loading safety data:', err);
      setError('Failed to load safety data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadSafetyData();
    setRefreshing(false);
  };

  const getDriverName = (driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    return driver?.name || driver?.username || `Driver ${driverId}`;
  };

  const getSafetyScoreColor = (score) => {
    if (score === null || score === undefined) return 'default';
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getScoreTrend = (score, previousScore) => {
    if (previousScore === null || previousScore === undefined) return 'neutral';
    if (score > previousScore) return 'up';
    if (score < previousScore) return 'down';
    return 'neutral';
  };

  const getScoreIcon = (score) => {
    if (score >= 90) return <Star color="success" />;
    if (score >= 80) return <CheckCircle color="success" />;
    if (score >= 60) return <Warning color="warning" />;
    return <Warning color="error" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const sortedScores = [...safetyScores].sort((a, b) => {
    switch (sortBy) {
      case 'score':
        return (b.score || 0) - (a.score || 0);
      case 'name':
        return getDriverName(a.driverId).localeCompare(getDriverName(b.driverId));
      case 'trend':
        return (b.score || 0) - (a.score || 0); // Sort by score for trend view
      default:
        return 0;
    }
  });

  const getTopPerformers = () => {
    return sortedScores.filter(score => score.score >= 80).slice(0, 5);
  };

  const getNeedsAttention = () => {
    return sortedScores.filter(score => score.score < 60).slice(0, 5);
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }>
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Header Controls */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Driver Safety Scores
            </Typography>
            <Box display="flex" gap={1}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateRange}
                  label="Date Range"
                  onChange={(e) => setDateRange(e.target.value)}
                >
                  <MenuItem value="7">Last 7 days</MenuItem>
                  <MenuItem value="30">Last 30 days</MenuItem>
                  <MenuItem value="90">Last 90 days</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="score">Safety Score</MenuItem>
                  <MenuItem value="name">Driver Name</MenuItem>
                  <MenuItem value="trend">Performance Trend</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>View</InputLabel>
                <Select
                  value={viewMode}
                  label="View"
                  onChange={(e) => setViewMode(e.target.value)}
                >
                  <MenuItem value="table">Table</MenuItem>
                  <MenuItem value="list">List</MenuItem>
                </Select>
              </FormControl>
              <Tooltip title="Refresh Data">
                <IconButton onClick={handleRefresh} disabled={refreshing}>
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Summary Cards */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CheckCircle color="success" />
                    <Typography variant="h6">
                      {sortedScores.filter(s => s.score >= 80).length}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      High Performers
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Warning color="warning" />
                    <Typography variant="h6">
                      {sortedScores.filter(s => s.score >= 60 && s.score < 80).length}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Average
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Warning color="error" />
                    <Typography variant="h6">
                      {sortedScores.filter(s => s.score < 60).length}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Need Attention
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Top Performers */}
      {getTopPerformers().length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <Star color="success" sx={{ mr: 1, verticalAlign: 'middle' }} />
              Top Performers
            </Typography>
            <List>
              {getTopPerformers().map((score, index) => (
                <ListItem key={score.driverId}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'success.main' }}>
                      {index + 1}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={getDriverName(score.driverId)}
                    secondary={`Safety Score: ${score.score}%`}
                  />
                  <ListItemSecondaryAction>
                    <Chip
                      label={`${score.score}%`}
                      color="success"
                      size="small"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Needs Attention */}
      {getNeedsAttention().length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="error">
              <Warning sx={{ mr: 1, verticalAlign: 'middle' }} />
              Needs Attention
            </Typography>
            <List>
              {getNeedsAttention().map((score, index) => (
                <ListItem key={score.driverId}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'error.main' }}>
                      <Warning />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={getDriverName(score.driverId)}
                    secondary={`Safety Score: ${score.score}% - Requires immediate attention`}
                  />
                  <ListItemSecondaryAction>
                    <Chip
                      label={`${score.score}%`}
                      color="error"
                      size="small"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Main Scores Table/List */}
      <Card>
        <CardContent>
          {viewMode === 'table' ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Driver</TableCell>
                    <TableCell>Safety Score</TableCell>
                    <TableCell>Trend</TableCell>
                    <TableCell>Last Updated</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedScores.map((score) => {
                    const trend = getScoreTrend(score.score, score.previousScore);
                    return (
                      <TableRow key={score.driverId}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar sx={{ width: 32, height: 32 }}>
                              <Person />
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2">
                                {getDriverName(score.driverId)}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                ID: {score.driverId}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getScoreIcon(score.score)}
                            <Chip
                              label={`${score.score}%`}
                              color={getSafetyScoreColor(score.score)}
                              size="small"
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          {trend === 'up' && <TrendingUp color="success" />}
                          {trend === 'down' && <TrendingDown color="error" />}
                          {trend === 'neutral' && <Typography variant="body2" color="textSecondary">—</Typography>}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(score.updatedAt || score.timestamp)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Details">
                            <IconButton size="small">
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <List>
              {sortedScores.map((score) => (
                <ListItem key={score.driverId} divider>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: getSafetyScoreColor(score.score) + '.main' }}>
                      {getScoreIcon(score.score)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={getDriverName(score.driverId)}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Safety Score: {score.score}%
                        </Typography>
                        <Box mt={1}>
                          <LinearProgress
                            variant="determinate"
                            value={score.score}
                            color={getSafetyScoreColor(score.score)}
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                        </Box>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={`${score.score}%`}
                        color={getSafetyScoreColor(score.score)}
                        size="small"
                      />
                      <Tooltip title="View Details">
                        <IconButton size="small">
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}

          {safetyScores.length === 0 && (
            <Box textAlign="center" py={4}>
              <Alert severity="info">
                No safety score data available. Configure Samsara integration to view driver safety scores.
              </Alert>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DriverSafetyScores;
